import axios from 'axios';
import forIn from 'lodash/forIn';

import * as AnalyticsConstants from './constants';
import * as D from './deps';

const logger = D.Logger.child({ component: 'analytics/utils' });
const PageViews = AnalyticsConstants.StatsPageViewNames;
const pathMapping = {
  [PageViews.NOW_PLAYING]: (path, state) =>
    D.Utils.Route.isPathInNowPlayingPath(path) && !D.Utils.UIState.getIsSessionHistoryOpen(state),
  [PageViews.MY_STATIONS]: path => path === D.Utils.Route.getMyStationsPath(),
  [PageViews.SESSION_HISTORY]: (path, state) => D.Utils.UIState.getIsSessionHistoryOpen(state),
  [PageViews.BACKSTAGE_TRACK]: path => D.Utils.Route.isBackstageTrackPath(path),
  [PageViews.BACKSTAGE_TRACK_LYRICS]: path => D.Utils.Route.isBackstageTrackLyricsPath(path),
  [PageViews.BACKSTAGE_TRACK_CREDITS]: path => D.Utils.Route.isBackstageTrackCreditsPath(path),
  [PageViews.BACKSTAGE_TRACK_SIMILAR_SONGS]: path =>
    D.Utils.Route.isBackstageTrackSimilarSongsPath(path),
  [PageViews.BACKSTAGE_GENRE]: path => D.Utils.Route.isBackstageGenrePath(path),
  [PageViews.BACKSTAGE_ARTIST]: path => D.Utils.Route.isBackstageArtistPath(path),
  [PageViews.BACKSTAGE_ARTIST_FULL_BIO]: path => D.Utils.Route.isBackstageArtistFullBioPath(path),
  [PageViews.BACKSTAGE_ARTIST_ALL_SONGS]: path => D.Utils.Route.isBackstageArtistAllSongsPath(path),
  [PageViews.BACKSTAGE_ARTIST_ALL_ALBUMS]: path =>
    D.Utils.Route.isBackstageArtistAllAlbumsPath(path),
  [PageViews.BACKSTAGE_ARTIST_ALL_PLAYLISTS]: path =>
    D.Utils.Route.isBackstageArtistAllPlaylistsPath(path),
  [PageViews.BACKSTAGE_ARTIST_ALL_STATIONS]: path =>
    D.Utils.Route.isBackstageArtistAllStationsPath(path),
  [PageViews.BACKSTAGE_ALBUM]: path => D.Utils.Route.isBackstageAlbumPath(path),
  [PageViews.BACKSTAGE_ALBUM_DESCRIPTION]: path =>
    D.Utils.Route.isBackstageAlbumDescriptionPath(path),
  [PageViews.BACKSTAGE_CURATOR]: path => D.Utils.Route.isBackstageCuratorPath(path),
  [PageViews.BACKSTAGE_CURATOR_ALL_PLAYLISTS]: path =>
    D.Utils.Route.isBackstageCuratorAllPlaylistsPath(path),
  [PageViews.BACKSTAGE_CURATOR_ALL_STATIONS]: path =>
    D.Utils.Route.isBackstageCuratorAllStationsPath(path),
  [PageViews.BACKSTAGE_PODCAST_PROGRAM]: path => D.Utils.Route.isBackstagePodcastProgramPath(path),
  [PageViews.BACKSTAGE_PODCAST_EPISODE]: path => D.Utils.Route.isBackstagePodcastEpisodePath(path),
  [PageViews.BACKSTAGE_PODCAST_THUMBED_UP_EPISODES]: path =>
    D.Utils.Route.isBackstagePodcastThumbedUpEpisodesPath(path),
  [PageViews.BACKSTAGE_PODCAST_THUMBED_DOWN_EPISODES]: path =>
    D.Utils.Route.isBackstagePodcastThumbedDownEpisodesPath(path),
  [PageViews.BACKSTAGE_PODCAST_ALL_EPISODES]: path =>
    D.Utils.Route.isBackstagePodcastAllEpisodesPath(path),
  [PageViews.BACKSTAGE_PODCAST_PROGRAM_DESCRIPTION]: path =>
    D.Utils.Route.isBackstagePodcastProgramDescriptionPath(path),
  [PageViews.BACKSTAGE_PODCAST_EPISODE_DESCRIPTION]: path =>
    D.Utils.Route.isBackstagePodcastEpisodeDescriptionPath(path),
  [PageViews.SEARCH]: path => D.Utils.Route.isInSearchPath(path),
  [PageViews.LANDING_PAGE]: path => path === D.Utils.Route.getLandingPath(),
  [PageViews.ACCOUNT_SETTINGS]: path => path === D.Utils.Route.getInfoPath(),
  [PageViews.CONTENT_SETTINGS]: path => path === D.Utils.Route.getContentSettingsPath(),
  [PageViews.NOTIFICATION_SETTINGS]: path => path === D.Utils.Route.getNotificationsPath(),
  [PageViews.PAYMENT_PLUS]: path => path === D.Utils.Route.getPlusPaymentPath(),
  [PageViews.PAYMENT_PREMIUM]: path => path === D.Utils.Route.getPremiumPaymentPath(),
  [PageViews.PAYMENT_STUDENT]: path => path === D.Utils.Route.getPremiumStudentPlanPaymentPath(),
  [PageViews.PAYMENT_MILITARY]: path => path === D.Utils.Route.getPremiumMilitaryPlanPaymentPath(),
  [PageViews.PAYMENT_FAMILY]: path => path === D.Utils.Route.getFamilyPlanPaymentPath(),
  [PageViews.UPGRADE]: path => D.Utils.Route.isUpgradeOrSettingsUpgradePage(path),
  [PageViews.UPGRADE_LANDING]: path =>
    D.Utils.Route.isUpgradeOrSettingsUpgradePage(path) || path === D.Utils.Route.getPlansPath(),
  [PageViews.UPGRADE_PREMIUM]: path =>
    path === D.Utils.Route.getPremiumUpgradePath() || path === D.Utils.Route.getPremiumInvitePath(),
  [PageViews.UPGRADE_PREMIUM_PAGE]: path => path === D.Utils.Route.getPremiumUpgradePath(),
  [PageViews.UPGRADE_STUDENT]: path => path === D.Utils.Route.getPremiumStudentPlanUpgradePath(),
  [PageViews.UPGRADE_MILITARY]: path => path === D.Utils.Route.getPremiumMilitaryPlanUpgradePath(),
  [PageViews.UPGRADE_FAMILY_PLAN]: path => path === D.Utils.Route.getFamilyPlanUpgradePath(),
  [PageViews.UPGRADE_FAMILY]: path => path === D.Utils.Route.getFamilyPlanUpgradePath(),
  [PageViews.UPGRADE_PLUS]: path => path === D.Utils.Route.getPlusUpgradePath(),
  [PageViews.VERIFICATION_STUDENT]: path =>
    path === D.Utils.Route.getPremiumStudentPlanVerificationPath(),
  [PageViews.VERIFICATION_MILITARY]: path =>
    path === D.Utils.Route.getPremiumMilitaryPlanVerificationPath(),
  [PageViews.SUBSCRIPTION]: path => path === D.Utils.Route.getSubscriptionPath(),
  [PageViews.SUBSCRIPTION_WELCOME]: path => path === D.Utils.Route.getSubscriptionWelcomePath(),
  [PageViews.REDEEM_CODE]: path => path === D.Utils.Route.getRedeemPath(),
  [PageViews.PROFILE]: path => D.Utils.Route.isProfilePath(path),
  [PageViews.PROFILE_THUMBS]: path => path.includes(D.Utils.Route.getProfileThumbsPath()),
  [PageViews.REGISTRATION]: path => path === D.Utils.Route.getRegisterPath(),
  [PageViews.REGISTRATION_BLOCKED]: path => path === D.Utils.Route.getRegistrationBlockedPath(),
  [PageViews.BROWSE]: path => path === D.Utils.Route.getBrowsePath(),
  [PageViews.LOGIN]: path => path === D.Utils.Route.getLoginPath(),
  [PageViews.LOGIN_SUCCESS]: path => path === D.Utils.Route.getMobileSignInWelcomePath(),
  [PageViews.LOGOUT]: path => path === D.Utils.Route.getLogoutPath(),
  [PageViews.FORGOT_PASSWORD]: path => path === D.Utils.Route.getForgotPasswordPath(),
  [PageViews.RESET_PASSWORD]: path => path === D.Utils.Route.getResetPasswordPath(),
  [PageViews.STATION_DETAILS]: path => D.Utils.Route.isStationDetailsPath(path),
  [PageViews.SERVICE_ERROR]: path => path === D.Utils.Route.getServiceErrorPath(),
  [PageViews.PLAYLIST]: path => D.Utils.Route.isPlaylistPath(path),
  [PageViews.MY_MUSIC]: path =>
    D.Utils.Route.isMyMusicPath(path) || D.Utils.Route.isPathInMyMusic(path),
  [PageViews.ATT_PREMIUM]: path => D.Utils.Route.isAttPremiumPath(path),
  [PageViews.PARTNER_UPGRADE]: path => D.Utils.Route.isPartnerUpgradePath(path),
  [PageViews.FAMILY_PLAN]: path => D.Utils.Route.isFamilyPlanPath(path),
  [PageViews.COLLECTION]: path => D.Utils.Route.isCollectionPath(path),
};

export function mapPathToPageView(state, path) {
  let pageView;
  forIn(pathMapping, (matchFunc, key) => {
    if (matchFunc(path, state)) {
      pageView = key;
      return;
    }
  });

  if (pageView === undefined) {
    logger.info(`No page_view mapping found for path: ${path}`);
  }

  return pageView;
}

// used to ensure that only responses with status 200 are accepted
function validateStatus(status) {
  return status === 200;
}

// for reuse
const pingXhr = axios.create({ validateStatus });

export function pingTrackingUrl(url) {
  if (!D.Utils.Url.isValidUrl(url)) {
    return Promise.resolve(new TypeError('Invalid url supplied as tracking url'));
  }
  const target = D.Utils.Url.secureUrl(url);
  // @note this is an attempt at a cors request without flash
  // any status other than 200 on the response will cause it to be rejected
  return pingXhr.get(target).catch(e => {
    return Promise.resolve(e);
  });
}
